import React, { useState } from 'react';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

const FAQAccordion = ({ 
  title, 
  items = [], 
  iconType = 'plusMinus', // 'plusMinus', 'expandMore', 'arrow', 'arrowDrop'
  iconColor = '#36C3D6',
  iconSize = 'large',
  showIcon = true,
  // New styling options
  textAlign = 'left', // 'left', 'center', 'right'
  containerBackgroundColor = 'transparent',
  questionBackgroundColor = 'transparent',
  answerBackgroundColor = 'rgba(0, 0, 0, 0.02)',
  questionTextColor = '#4B329A',
  answerTextColor = '#4B329A',
  titleTextColor = 'primary',
  showDivider = true,
  dividerColor = '#C1A7F1',
  itemBorderRadius = '4px',
  itemSpacing = '12px',
  questionPadding = '16px',
  answerPadding = '16px'
}) => {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (idx) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  // Icon configurations
  const getIcons = () => {
    switch (iconType) {
      case 'expandMore':
        return {
          collapsed: <ExpandMoreIcon />,
          expanded: <ExpandLessIcon />
        };
      case 'arrow':
        return {
          collapsed: <KeyboardArrowDownIcon />,
          expanded: <KeyboardArrowUpIcon />
        };
      case 'arrowDrop':
        return {
          collapsed: <ArrowDropDownIcon />,
          expanded: <ArrowDropUpIcon />
        };
      case 'plusMinus':
      default:
        return {
          collapsed: <AddCircleOutlineIcon />,
          expanded: <RemoveCircleOutlineIcon />
        };
    }
  };

  const icons = getIcons();

  return (
    <Box sx={{ 
      my: 4,
      backgroundColor: containerBackgroundColor,
      borderRadius: itemBorderRadius,
      padding: containerBackgroundColor !== 'transparent' ? 2 : 0
    }}>
      {title && (
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ 
            textAlign: textAlign === 'center' ? 'center' : textAlign, 
            mb: 3 
          }}
          color={titleTextColor}
        >
          {title}
        </Typography>
      )}
      <Box>
        {items.map((item, idx) => (
          <Box key={idx} sx={{ mb: itemSpacing }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                py: 2,
                px: 2,
                backgroundColor: questionBackgroundColor,
                borderRadius: itemBorderRadius,
                padding: questionPadding,
                textAlign: textAlign,
                '&:hover': {
                  backgroundColor: questionBackgroundColor === 'transparent' 
                    ? 'rgba(0, 0, 0, 0.04)'
                    : 'rgba(0, 0, 0, 0.06)',
                  borderRadius: itemBorderRadius
                }
              }}
              onClick={() => handleToggle(idx)}
            >
              <Typography 
                variant="subtitle1" 
                fontWeight="bold" 
                color={questionTextColor}
                sx={{ 
                  flex: 1, 
                  pr: showIcon ? 2 : 0,
                  lineHeight: 1.4,
                  textAlign: textAlign
                }}
              >
                {item.question}
              </Typography>
              {showIcon && (
                <IconButton 
                  edge="end" 
                  size={iconSize} 
                  sx={{ 
                    color: iconColor,
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(54, 195, 214, 0.1)'
                    }
                  }}
                >
                  {openIndex === idx ? icons.expanded : icons.collapsed}
                </IconButton>
              )}
            </Box>
            {openIndex === idx && (
              <Typography 
                variant="body1" 
                sx={{ 
                  padding: answerPadding,
                  color: answerTextColor,
                  lineHeight: 1.6,
                  backgroundColor: answerBackgroundColor,
                  borderRadius: itemBorderRadius,
                  mt: 1,
                  textAlign: textAlign
                }}
              >
                {item.answer}
              </Typography>
            )}
            {showDivider && idx < items.length - 1 && (
              <Divider sx={{ 
                borderBottomWidth: 2, 
                borderColor: dividerColor,
                mt: 2
              }} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FAQAccordion; 